<template>
  <div class="quick-promotion-form">
    <h3 class="mb-4">
      {{ isEdit ? 'Edit' : 'Quick Add' }} Promotion
      <b-badge v-if="ratePlanId" variant="primary" class="ml-2">Scoped</b-badge>
    </h3>

    <b-form @submit.prevent="handleSubmit">
      <!-- Basic Info -->
      <b-card class="mb-3">
        <h5 class="mb-3">Basic Information</h5>

        <b-form-group label="Promotion Name *" label-for="name">
          <b-form-input
            id="name"
            v-model="form.name"
            placeholder="e.g., Summer Special"
            required
            @input="updateCodeFromName"
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Code *" label-for="code">
          <b-form-input
            id="code"
            v-model="form.code"
            placeholder="Auto-generated from name"
            required
            @focus="manualCodeEdit = true"
          ></b-form-input>
          <small class="text-muted">Auto-generated from name, you can edit it</small>
        </b-form-group>
      </b-card>

      <!-- Date & Time Settings -->
      <b-card class="mb-3">
        <h5 class="mb-3">Validity Period</h5>

        <b-row>
          <b-col md="6">
            <b-form-group label="Valid From" label-for="dateStart">
              <b-form-input
                id="dateStart"
                v-model="dateRange.start"
                type="date"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Valid Until" label-for="dateEnd">
              <b-form-input
                id="dateEnd"
                v-model="dateRange.end"
                type="date"
                :min="dateRange.start"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <!-- Pricing Modifier Type (applies to all routes) -->
      <b-card class="mb-3">
        <h5 class="mb-3">Price Adjustment Type</h5>
        <b-alert show variant="info" class="mb-3">
          <small>
            <md-icon icon="info" class="mr-1"></md-icon>
            This adjustment type will apply to all route configurations you add below.
          </small>
        </b-alert>

        <b-form-group label="Adjustment Type *" label-for="modifierType">
          <b-form-select
            id="modifierType"
            v-model="form.rateModifierType"
            :options="modifierTypeOptions"
            required
          ></b-form-select>
        </b-form-group>
      </b-card>

      <!-- Rate Plan & Route Selection -->
      <b-card class="mb-3">
        <h5 class="mb-3">Route Configuration</h5>

        <b-alert v-if="ratePlanId" show variant="info" class="mb-3">
          <small>
            <md-icon icon="info" class="mr-1"></md-icon>
            This promotion is scoped to the selected rate plan from the calendar.
          </small>
        </b-alert>

        <b-form-group label="Rate Plan *" label-for="ratePlan">
          <b-form-select
            id="ratePlan"
            v-model="form.ratePlanId"
            :options="ratePlanOptions"
            :disabled="!!ratePlanId"
            @change="onRatePlanChange"
            required
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                -- Select Rate Plan --
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <template v-if="form.ratePlanId">
          <b-form-group label="Service Type *" label-for="serviceType">
            <b-form-select
              id="serviceType"
              v-model="selectedSetting.serviceTypeId"
              :options="serviceTypeOptions"
              @change="updatePricePreview"
              required
            ></b-form-select>
          </b-form-group>

          <b-row>
            <b-col md="6">
              <b-form-group label="From Zone *" label-for="fromZone">
                <b-form-select
                  id="fromZone"
                  v-model="selectedSetting.fromZoneId"
                  :options="zoneOptions"
                  @change="updatePricePreview"
                  required
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="To Zone *" label-for="toZone">
                <b-form-select
                  id="toZone"
                  v-model="selectedSetting.toZoneId"
                  :options="zoneOptions"
                  @change="updatePricePreview"
                  required
                ></b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Ticket Type *" label-for="ticketType">
            <b-form-select
              id="ticketType"
              v-model="selectedSetting.ticket"
              :options="ticketTypeOptions"
              @change="updatePricePreview"
            ></b-form-select>
          </b-form-group>

          <!-- Price Preview -->
          <b-alert v-if="pricePreview" show variant="info" class="mb-0">
            <strong>Current Price:</strong>
            <span v-if="selectedSetting.ticket === 'oneway' || selectedSetting.ticket === ''">
              One-way: {{ pricePreview.currency }}{{ pricePreview.oneway }}
            </span>
            <span v-if="selectedSetting.ticket === 'roundtrip' || selectedSetting.ticket === ''">
              <span v-if="selectedSetting.ticket === ''"> | </span>
              Round-trip: {{ pricePreview.currency }}{{ pricePreview.roundtrip }}
            </span>
          </b-alert>

          <!-- Info for "All" selections -->
          <b-alert
            v-if="!pricePreview && form.ratePlanId && (selectedSetting.serviceTypeId === 0 || selectedSetting.fromZoneId === 0 || selectedSetting.toZoneId === 0)"
            show
            variant="secondary"
            class="mb-0"
          >
            <small>
              <md-icon icon="info" class="mr-1"></md-icon>
              Price preview is not available when using "All Zones" or "All Services" options.
            </small>
          </b-alert>
        </template>
      </b-card>

      <!-- Pricing Modifier Amount -->
      <b-card class="mb-3">
        <h5 class="mb-3">Price Adjustment Amount</h5>

        <b-alert v-if="form.rateModifierType === 'fixed_rate'" show variant="warning" class="mb-3">
          <small>
            <md-icon icon="info" class="mr-1"></md-icon>
            <strong>Fixed Rate is per ride:</strong> If roundtrip is selected, this price will be charged twice (once for each direction).
          </small>
        </b-alert>

        <b-row>
          <b-col md="6">
            <b-form-group
              :label="getModifierAmountLabel() + ' *'"
              label-for="modifierAmount"
            >
              <b-form-input
                id="modifierAmount"
                v-model.number="selectedSetting.modifierAmount"
                type="number"
                step="0.01"
                min="0"
                required
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="form.rateModifierType !== 'fixed_rate'">
            <b-form-group label="Apply As *" label-for="modifierSign">
              <b-form-select
                id="modifierSign"
                v-model="selectedSetting.modifierSign"
                :options="modifierSignOptions"
                required
              ></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-checkbox v-model="selectedSetting.applyReturn" class="mt-2">
          Apply to return journey (reverse direction)
        </b-form-checkbox>

        <!-- Price Result Preview -->
        <b-alert v-if="pricePreview && selectedSetting.modifierAmount" show variant="success" class="mt-3 mb-0">
          <strong>New Price:</strong>
          <span v-if="selectedSetting.ticket === 'oneway' || selectedSetting.ticket === ''">
            One-way: {{ pricePreview.currency }}{{ calculateNewPrice('oneway') }}
          </span>
          <span v-if="selectedSetting.ticket === 'roundtrip' || selectedSetting.ticket === ''">
            <span v-if="selectedSetting.ticket === ''"> | </span>
            Round-trip: {{ pricePreview.currency }}{{ calculateNewPrice('roundtrip') }}
          </span>
        </b-alert>

        <!-- Add to Cart Button -->
        <div class="mt-3">
          <b-alert
            v-if="form.rateModifierType === 'fixed_rate' && (selectedSetting.serviceTypeId === 0 || selectedSetting.fromZoneId === 0 || selectedSetting.toZoneId === 0)"
            show
            variant="warning"
            class="mb-2"
          >
            <small>
              <strong>Fixed Rate requires specific selections:</strong> You cannot use "All Zones" or "All Services" with Fixed Rate pricing.
            </small>
          </b-alert>

          <div class="text-right">
            <b-button
              variant="primary"
              @click="addSettingToCart"
              :disabled="!canAddSetting"
              size="sm"
            >
              <md-icon icon="add_shopping_cart" class="mr-1"></md-icon>
              Add Route Configuration
            </b-button>
          </div>
        </div>
      </b-card>

      <!-- Settings Cart -->
      <b-card v-if="form.settings.length > 0" class="mb-3">
        <h5 class="mb-3">
          Route Configurations
          <b-badge variant="success" class="ml-2">{{ form.settings.length }}</b-badge>
        </h5>

        <b-alert show variant="warning" class="mb-3" v-if="!form.settings.length">
          <small>
            <md-icon icon="info" class="mr-1"></md-icon>
            Add at least one route configuration to create the promotion.
          </small>
        </b-alert>

        <b-table
          :items="form.settings"
          :fields="settingsTableFields"
          striped
          hover
          small
          responsive
        >
          <template #cell(serviceType)="row">
            {{ getServiceTypeName(row.item.serviceTypeId) }}
          </template>

          <template #cell(fromZone)="row">
            {{ getZoneName(row.item.fromZoneId) }}
          </template>

          <template #cell(toZone)="row">
            {{ getZoneName(row.item.toZoneId) }}
          </template>

          <template #cell(ticket)="row">
            {{ getTicketTypeName(row.item.ticket) }}
          </template>

          <template #cell(modifier)="row">
            <span v-if="form.rateModifierType === 'fixed_rate'">
              {{ (pricePreview && pricePreview.currency) || '$' }}{{ row.item.modifierAmount }}
            </span>
            <span v-else>
              {{ row.item.modifierSign === 1 ? '+' : '-' }}{{ row.item.modifierAmount }}
              {{ form.rateModifierType === 'percent' ? '%' : '$' }}
            </span>
          </template>

          <template #cell(applyReturn)="row">
            <b-badge :variant="row.item.applyReturn ? 'success' : 'secondary'">
              {{ row.item.applyReturn ? 'Yes' : 'No' }}
            </b-badge>
          </template>

          <template #cell(actions)="row">
            <b-button
              size="sm"
              variant="outline-danger"
              @click="removeSettingFromCart(row.index)"
            >
              <md-icon icon="delete"></md-icon>
            </b-button>
          </template>
        </b-table>
      </b-card>

      <!-- Advanced Options (Collapsible) -->
      <b-card class="mb-3">
        <div @click="showAdvanced = !showAdvanced" class="d-flex justify-content-between align-items-center cursor-pointer">
          <h5 class="mb-0">Advanced Options</h5>
          <md-icon :icon="showAdvanced ? 'expand_less' : 'expand_more'"></md-icon>
        </div>

        <b-collapse v-model="showAdvanced" class="mt-3">
          <b-form-group label="Description (Optional)" label-for="description">
            <b-form-textarea
              id="description"
              v-model="form.description"
              rows="2"
              placeholder="Internal notes about this promotion"
            ></b-form-textarea>
          </b-form-group>

          <b-row>
            <b-col md="6">
              <b-form-group label="Min Hours in Advance" label-for="hoursMin">
                <b-form-input
                  id="hoursMin"
                  v-model.number="form.hoursInAdvanceMin"
                  type="number"
                  min="0"
                  placeholder="0"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Max Hours in Advance" label-for="hoursMax">
                <b-form-input
                  id="hoursMax"
                  v-model.number="form.hoursInAdvanceMax"
                  type="number"
                  min="0"
                  placeholder="Unlimited"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Coupon Code (Optional)" label-for="token">
            <b-form-input
              id="token"
              v-model="form.token"
              placeholder="e.g., SAVE10"
            ></b-form-input>
          </b-form-group>

          <b-form-group
            v-if="form.token"
            label="Coupon Usage Limit"
            label-for="tokenLimit"
          >
            <b-form-input
              id="tokenLimit"
              v-model.number="form.tokenLimit"
              type="number"
              min="1"
              placeholder="Unlimited"
            ></b-form-input>
          </b-form-group>

          <b-form-group label="Priority" label-for="priority">
            <b-form-input
              id="priority"
              v-model.number="form.priority"
              type="number"
              min="0"
              placeholder="0 (higher = higher priority)"
            ></b-form-input>
          </b-form-group>

          <b-form-group label="Sales Channels (Optional)" label-for="salesChannels">
            <b-form-select
              id="salesChannels"
              v-model="form.salesChannels"
              :options="salesChannelOptions"
              multiple
              :select-size="4"
            ></b-form-select>
            <small class="text-muted d-block mt-1">Leave empty for all channels</small>
          </b-form-group>
        </b-collapse>
      </b-card>

      <!-- Actions -->
      <div class="d-flex justify-content-end gap-2">
        <b-button variant="secondary" @click="$emit('cancel')">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          type="submit"
          :disabled="loading || !isFormValid"
        >
          <b-spinner small v-if="loading"></b-spinner>
          {{ loading ? 'Saving...' : (isEdit ? 'Update' : 'Create') }} Promotion
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
export default {
  name: 'QuickPromotionForm',
  props: {
    embeddedOn: {
      type: String,
      required: true,
      validator: value => ['supplier', 'sales-channel'].includes(value)
    },
    embeddedOnId: {
      type: Number,
      required: true
    },
    promotionId: {
      type: Number,
      default: null
    },
    ratePlanId: {
      type: Number,
      default: null
    },
    salesChannelId: {
      type: Number,
      default: null
    },
    selectedDate: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      showAdvanced: false,
      manualCodeEdit: false, // Track if user manually edited the code

      form: {
        name: '',
        code: '',
        description: '',
        ratePlanId: null,
        rateModifierType: 'percent',
        days: [0, 1, 2, 3, 4, 5, 6], // All days by default
        hoursInAdvanceMin: null,
        hoursInAdvanceMax: null,
        token: '',
        tokenLimit: null,
        priority: 0,
        salesChannels: [],
        scheduleStart: null,
        scheduleEnd: null,
        dates: {
          bookingWindow: [],
          bookingWindowBlockout: [],
          travelWindow: [],
          travelWindowBlockout: [],
          assignedWindow: [],
          assignedWindowBlockout: []
        },
        settings: []
      },

      selectedSetting: {
        serviceTypeId: 0,
        fromZoneId: 0,
        toZoneId: 0,
        ticket: '',
        modifierAmount: null,
        modifierSign: -1,
        applyReturn: false
      },

      settingsTableFields: [
        { key: 'serviceType', label: 'Service Type' },
        { key: 'fromZone', label: 'From Zone' },
        { key: 'toZone', label: 'To Zone' },
        { key: 'ticket', label: 'Ticket' },
        { key: 'modifier', label: 'Price Modifier' },
        { key: 'applyReturn', label: 'Apply Return' },
        { key: 'actions', label: 'Actions' }
      ],

      dateRange: {
        start: '',
        end: ''
      },

      ratePlans: [],
      ratePlanData: null,
      pricePreview: null,
      salesChannels: [],

      ticketTypeOptions: [
        { value: '', text: 'All Tickets' },
        { value: 'oneway', text: 'One-way Only' },
        { value: 'roundtrip', text: 'Round-trip Only' }
      ],

      modifierTypeOptions: [
        { value: 'percent', text: 'Percentage (%)' },
        { value: 'amount', text: 'Fixed Amount ($)' },
        { value: 'fixed_rate', text: 'Fixed Rate (Set Price)' }
      ],

      modifierSignOptions: [
        { value: -1, text: 'Discount (Subtract)' },
        { value: 1, text: 'Surcharge (Add)' }
      ]
    }
  },

  computed: {
    isEdit() {
      return !!this.promotionId
    },

    ratePlanOptions() {
      return this.ratePlans.map(rp => ({
        value: rp.id,
        text: rp.title || rp.name // API returns 'title', not 'name'
      }))
    },

    serviceTypeOptions() {
      if (!this.ratePlanData?.serviceTypes) return []
      const allOption = [{ value: 0, text: 'All Services' }]
      const serviceTypes = this.ratePlanData.serviceTypes.map(st => ({
        value: st.id,
        text: st.name
      }))
      return [...allOption, ...serviceTypes]
    },

    zoneOptions() {
      // For sales-channel, zones are under zonedArea.zones
      // For supplier, zones are under supplierZonedArea.zones
      let zones = []
      if (this.embeddedOn === 'supplier' && this.ratePlanData?.supplierZonedArea?.zones) {
        zones = this.ratePlanData.supplierZonedArea.zones
      } else if (this.embeddedOn === 'sales-channel' && this.ratePlanData?.zonedArea?.zones) {
        zones = this.ratePlanData.zonedArea.zones
      }

      const allOption = [{ value: 0, text: 'All Zones' }]
      const zoneList = zones.map(z => ({
        value: z.id,
        text: z.name
      }))
      return [...allOption, ...zoneList]
    },

    salesChannelOptions() {
      return this.salesChannels.map(sc => ({
        value: sc.id,
        text: sc.name
      }))
    },

    isFormValid() {
      return this.form.name &&
             this.form.code &&
             this.form.rateModifierType &&
             this.form.ratePlanId &&
             this.form.settings.length > 0 // At least one setting must be added
    },

    canAddSetting() {
      // For fixed_rate, must have specific zones and service type
      if (this.form.rateModifierType === 'fixed_rate') {
        if (this.selectedSetting.serviceTypeId === 0 ||
            this.selectedSetting.fromZoneId === 0 ||
            this.selectedSetting.toZoneId === 0) {
          return false
        }
      }

      return this.selectedSetting.serviceTypeId !== null &&
             this.selectedSetting.fromZoneId !== null &&
             this.selectedSetting.toZoneId !== null &&
             typeof this.selectedSetting.ticket !== 'undefined' && // Allow empty string for "All Tickets"
             this.selectedSetting.modifierAmount > 0
    }
  },

  async mounted() {
    await this.loadRatePlans()

    // Pre-populate rate plan if provided (from calendar context)
    if (this.ratePlanId && !this.isEdit) {
      this.form.ratePlanId = this.ratePlanId
      await this.onRatePlanChange()
    }

    // Pre-fill validity period with the selected calendar date
    if (this.selectedDate && !this.isEdit) {
      this.dateRange.start = this.selectedDate
      this.dateRange.end = this.selectedDate
    }

    if (this.isEdit) {
      await this.loadPromotion()
    }
  },

  methods: {
    updateCodeFromName() {
      // Auto-generate code from name if user hasn't manually edited it
      // Convert to uppercase, replace spaces with underscores, remove special chars
      if (!this.manualCodeEdit) {
        const code = this.form.name
          .toUpperCase()
          .replace(/\s+/g, '_')
          .replace(/[^A-Z0-9_]/g, '')
          .substring(0, 50) // Limit length

        this.form.code = code
      }
    },

    async loadRatePlans() {
      try {
        const response = await this.$root.api.get(
          `/util/promotions/${this.embeddedOn}/${this.embeddedOnId}/rate-plans-for-promotions`
        )
        this.ratePlans = response.data || []
      } catch (error) {
        console.error('Error loading rate plans:', error)
        this.$root.showAlert('Failed to load rate plans', { variant: 'danger' })
      }
    },

    async onRatePlanChange() {
      if (!this.form.ratePlanId) return

      try {
        const response = await this.$root.api.get(
          `/util/promotions/rate-plans/${this.form.ratePlanId}`
        )
        this.ratePlanData = response.data
      } catch (error) {
        console.error('Error loading rate plan data:', error)
        this.$root.showAlert('Failed to load rate plan details', { variant: 'danger' })
      }
    },

    async updatePricePreview() {
      // Only show price preview for specific zones and services (not "All")
      if (!this.form.ratePlanId ||
          this.selectedSetting.serviceTypeId === 0 ||
          this.selectedSetting.fromZoneId === 0 ||
          this.selectedSetting.toZoneId === 0) {
        this.pricePreview = null
        return
      }

      try {
        const params = {
          filters: {
            serviceTypeId: this.selectedSetting.serviceTypeId,
            fromZoneId: this.selectedSetting.fromZoneId,
            toZoneId: this.selectedSetting.toZoneId
          }
        }

        const response = await this.$root.api.get(
          `/util/promotions/rate-plans/${this.form.ratePlanId}/get-price`,
          { params }
        )
        this.pricePreview = response.data
      } catch (error) {
        console.error('Error loading price preview:', error)
        this.pricePreview = null
      }
    },

    calculateNewPrice(ticketType) {
      if (!this.pricePreview || !this.selectedSetting.modifierAmount) return 0

      const currentPrice = this.pricePreview[ticketType]
      const amount = parseFloat(this.selectedSetting.modifierAmount)

      if (this.form.rateModifierType === 'fixed_rate') {
        return amount.toFixed(2)
      } else if (this.form.rateModifierType === 'percent') {
        const change = currentPrice * (amount / 100)
        const newPrice = currentPrice + (change * this.selectedSetting.modifierSign)
        return Math.max(0, newPrice).toFixed(2)
      } else { // amount
        const newPrice = currentPrice + (amount * this.selectedSetting.modifierSign)
        return Math.max(0, newPrice).toFixed(2)
      }
    },

    getModifierAmountLabel() {
      switch (this.form.rateModifierType) {
        case 'percent':
          return 'Percentage'
        case 'amount':
          return 'Amount'
        case 'fixed_rate':
          return 'Fixed Price'
        default:
          return 'Amount'
      }
    },

    async loadPromotion() {
      try {
        this.loading = true
        const response = await this.$root.api.get(
          `/promotions/${this.embeddedOn}/${this.embeddedOnId}/${this.promotionId}`
        )
        const promotion = response.data.data

        // Populate form
        this.form.name = promotion.name
        this.form.code = promotion.code
        this.form.description = promotion.description
        this.form.ratePlanId = promotion.ratePlanId
        this.form.rateModifierType = promotion.rateModifierType
        this.form.days = promotion.days || [0, 1, 2, 3, 4, 5, 6]
        this.form.hoursInAdvanceMin = promotion.hoursInAdvanceMin
        this.form.hoursInAdvanceMax = promotion.hoursInAdvanceMax
        this.form.token = promotion.token
        this.form.tokenLimit = promotion.tokenLimit
        this.form.priority = promotion.priority
        this.form.salesChannels = promotion.salesChannels || []
        this.form.dates = promotion.dates || this.form.dates

        // Load rate plan data
        await this.onRatePlanChange()

        // Populate settings if exists
        if (promotion.settings && promotion.settings.length > 0) {
          this.form.settings = promotion.settings.map(setting => ({
            serviceTypeId: setting.serviceTypeId,
            fromZoneId: setting.fromZoneId,
            toZoneId: setting.toZoneId,
            ticket: setting.ticket,
            modifierAmount: setting.modifierAmount,
            modifierSign: setting.modifierSign,
            applyReturn: setting.applyReturn
          }))
        }

        // Extract date range from travel window
        if (promotion.dates?.travelWindow && promotion.dates.travelWindow.length > 0) {
          this.dateRange.start = promotion.dates.travelWindow[0].start
          this.dateRange.end = promotion.dates.travelWindow[0].end
        }

      } catch (error) {
        console.error('Error loading promotion:', error)
        this.$root.showAlert('Failed to load promotion', { variant: 'danger' })
      } finally {
        this.loading = false
      }
    },

    addSettingToCart() {
      if (!this.canAddSetting) return

      const newSetting = {
        serviceTypeId: this.selectedSetting.serviceTypeId,
        fromZoneId: this.selectedSetting.fromZoneId,
        toZoneId: this.selectedSetting.toZoneId,
        ticket: this.selectedSetting.ticket,
        modifierAmount: this.selectedSetting.modifierAmount,
        modifierSign: this.form.rateModifierType === 'fixed_rate'
          ? 1
          : this.selectedSetting.modifierSign,
        applyReturn: this.selectedSetting.applyReturn
      }

      // Check for duplicates
      const isDuplicate = this.form.settings.some(setting => {
        return setting.serviceTypeId === newSetting.serviceTypeId &&
               setting.fromZoneId === newSetting.fromZoneId &&
               setting.toZoneId === newSetting.toZoneId &&
               setting.ticket === newSetting.ticket
      })

      // Check for reverse duplicates if applyReturn is true
      const hasReverseConflict = this.form.settings.some(setting => {
        if (setting.applyReturn) {
          return setting.serviceTypeId === newSetting.serviceTypeId &&
                 setting.fromZoneId === newSetting.toZoneId &&
                 setting.toZoneId === newSetting.fromZoneId &&
                 setting.ticket === newSetting.ticket
        }
        return false
      })

      if (isDuplicate || hasReverseConflict) {
        this.$root.showAlert('This route configuration already exists in the list', {
          variant: 'info'
        })
        return
      }

      // Add to settings
      this.form.settings.push(newSetting)

      // Reset the form
      this.selectedSetting = {
        serviceTypeId: 0,
        fromZoneId: 0,
        toZoneId: 0,
        ticket: '',
        modifierAmount: this.selectedSetting.modifierAmount, // Keep the amount
        modifierSign: this.selectedSetting.modifierSign, // Keep the sign
        applyReturn: false
      }

      this.$root.showAlert('Route configuration added successfully', {
        variant: 'success'
      })
    },

    removeSettingFromCart(index) {
      this.form.settings.splice(index, 1)
    },

    getServiceTypeName(serviceTypeId) {
      if (serviceTypeId === 0) return 'All Services'
      const serviceType = this.serviceTypeOptions.find(st => st.value === serviceTypeId)
      return serviceType ? serviceType.text : 'Unknown'
    },

    getZoneName(zoneId) {
      if (zoneId === 0) return 'All Zones'
      const zone = this.zoneOptions.find(z => z.value === zoneId)
      return zone ? zone.text : 'Unknown'
    },

    getTicketTypeName(ticket) {
      if (ticket === '') return 'All Tickets'
      if (ticket === 'oneway') return 'One-way'
      if (ticket === 'roundtrip') return 'Round-trip'
      return ticket
    },

    async handleSubmit() {
      if (!this.isFormValid) {
        this.$root.showAlert('Please add at least one route configuration', {
          variant: 'warning'
        })
        return
      }

      try {
        this.loading = true

        // Build date windows if date range is set
        if (this.dateRange.start && this.dateRange.end) {
          this.form.dates.travelWindow = [{
            start: this.dateRange.start,
            end: this.dateRange.end
          }]
        }

        // Make API call
        const url = this.isEdit
          ? `/promotions/${this.embeddedOn}/${this.embeddedOnId}/${this.promotionId}`
          : `/promotions/${this.embeddedOn}/${this.embeddedOnId}`

        const method = this.isEdit ? 'put' : 'post'

        await this.$root.api[method](url, this.form)

        this.$root.showAlert(
          `Promotion ${this.isEdit ? 'updated' : 'created'} successfully`,
          { variant: 'success' }
        )

        this.$emit('success')

      } catch (error) {
        console.error('Error saving promotion:', error)
        this.$root.showAlert(
          error.response?.data?.message || 'Failed to save promotion',
          { variant: 'danger' }
        )
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.quick-promotion-form {
  max-width: 900px;
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}

.gap-2 {
  gap: 0.5rem;
}
</style>
